<template>
  <div class="w-full md:w-8 lg:w-8">
    <div class="w-full mb-5">
      <h3 class="Label mb-4 text-left">Parents/Guardians name<span class="required-astrterick"> *</span></h3>
      <InputText v-model="form.guardians[0].name" placeholder="Enter parent/guardian name here" size="small" variant="filled" class="w-full" />
    </div>

    <div class="w-full mb-5">
      <h3 class="Label mb-4 text-left">Parents/Guardians number<span class="required-astrterick"> *</span></h3>
      <div class="flex gap-2 w-full">
        <Select v-model="form.guardians[0].phoneCountryCode" :options="countryCodeOptions" optionLabel="name"
          optionValue="code" placeholder="Code" class="phone-country-select" />
        <InputText v-model="form.guardians[0].phoneNumber" placeholder="Enter parent/guardian number here" size="small" variant="filled" 
          class="w-full" type="tel" maxlength="15" @keypress="onlyAllowNumbers" />
      </div>
    </div>

    <div class="w-full mb-5">
      <h3 class="Label mb-4 text-left">Parents/Guardians email<span class="required-astrterick"> *</span></h3>
      <InputText v-model="form.guardians[0].email" placeholder="Enter parent/guardian email here" size="small" variant="filled" class="w-full" />
    </div>

    <div v-if="showSecondary" class="flex align-items-center mb-5">
      <Checkbox v-model="form.guardians[0].isPrimary" :binary="true" inputId="p1" class="orange-checkbox" @change="setPrimary(0)" />
      <label for="p1" class="ml-2 text-sm cursor-pointer">Set as Primary Guardian</label>
    </div>

    <div v-if="showSecondary" class="w-full mt-2">
      <div class="flex justify-content-between align-items-center mb-4">
        <h3 class="Label m-0 text-left">Secondary Parents/Guardians Details</h3>
        <Button class="p-button-danger p-button-text p-button-sm bg-red-100 border-none p-button-rounded p-1" @click="removeSecondary" icon="pi pi-times" />
      </div>

      <div class="w-full mb-5">
        <h3 class="Label mb-4 text-left">Parents/Guardians name<span class="required-astrterick"> *</span></h3>
        <InputText v-model="form.guardians[1].name" placeholder="Enter secondary parent/guardian name" size="small" variant="filled" class="w-full" />
      </div>

      <div class="w-full mb-5">
        <h3 class="Label mb-4 text-left">Parents/Guardians Number<span class="required-astrterick"> *</span></h3>
        <div class="flex gap-2 w-full">
          <Select v-model="form.guardians[1].phoneCountryCode" :options="countryCodeOptions" optionLabel="name"
            optionValue="code" placeholder="Code" class="phone-country-select" />
          <InputText v-model="form.guardians[1].phoneNumber" placeholder="Enter secondary parent/guardian number here" size="small" variant="filled" 
            class="w-full" type="tel" maxlength="15" @keypress="onlyAllowNumbers" />
        </div>
      </div>

      <div class="w-full mb-5">
        <h3 class="Label mb-4 text-left">Parents/Guardians Email<span class="required-astrterick"> *</span></h3>
        <InputText v-model="form.guardians[1].email" placeholder="Enter secondary parent/guardian email" size="small" variant="filled" class="w-full" />
      </div>

      <div class="flex align-items-center mb-6">
        <Checkbox v-model="form.guardians[1].isPrimary" :binary="true" inputId="p2" class="orange-checkbox" @change="setPrimary(1)" />
        <label for="p2" class="ml-2 text-sm cursor-pointer">Set as Primary Guardian</label>
      </div>
    </div>

    <div v-if="!showSecondary" class="flex align-items-center justify-content-center cursor-pointer mt-4 mb-6" @click="showSecondary = true">
      <i class="pi pi-plus text-xs add-link p-2 border-round"></i>
      <span class="font-medium ml-2">Add parent/ guardian details</span>
    </div>

    <p class="guardian-note orange-button-text">
      At least one legal guardian's contact information must be provided to complete registration.
    </p>

    <Button label="Next" class="orange-button mt-4" @click="nextStep" />
  </div>
</template>

<script setup>
import { ref, defineEmits } from "vue";
import InputText from "primevue/inputtext";
import Select from "primevue/select";
import Button from "primevue/button";
import Checkbox from "primevue/checkbox";
import { notifyWarn } from "@/utils/notification";
import { countryCodeOptions } from "@/utils/countryCodeOptions";

const emit = defineEmits(["parents-details-selected"]);

const showSecondary = ref(false);

const form = ref({
  guardians: [
    { name: "", phoneCountryCode: "+94", phoneNumber: "", email: "", isPrimary: true },
    { name: "", phoneCountryCode: "+94", phoneNumber: "", email: "", isPrimary: false }
  ]
});

const onlyAllowNumbers = (event) => {
  const char = String.fromCharCode(event.which);
  if (!/[0-9]/.test(char)) event.preventDefault();
};

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);


const setPrimary = (index) => {
  if (form.value.guardians[index].isPrimary) {
    form.value.guardians.forEach((g, i) => {
      if (i !== index) g.isPrimary = false;
    });
  } else {
    const otherIndex = index === 0 ? 1 : 0;
    form.value.guardians[otherIndex].isPrimary = true;
  }
};

const buildContactNumber = (guardian) => `${guardian.phoneCountryCode}${guardian.phoneNumber || ""}`;

const removeSecondary = () => {
  showSecondary.value = false;
  form.value.guardians[1] = { name: "", phoneCountryCode: "+94", phoneNumber: "", email: "", isPrimary: false };
  form.value.guardians[0].isPrimary = true;
};

const nextStep = () => {
  const g1 = form.value.guardians[0];
  const g2 = form.value.guardians[1];

  // Validation
  if (!g1.name) return notifyWarn("Please enter parent/guardian name");
  if (!g1.phoneNumber || g1.phoneNumber.length < 6) return notifyWarn("Please enter a valid primary number");
  if (!isValidEmail(g1.email)) return notifyWarn("Please enter a valid primary email");

  if (showSecondary.value) {
    if (!g2.name) return notifyWarn("Please enter secondary name");
    if (!g2.phoneNumber || g2.phoneNumber.length < 6) return notifyWarn("Please enter a valid secondary number");
    if (!isValidEmail(g2.email)) return notifyWarn("Please enter a valid secondary email");
    
    if (!g1.isPrimary && !g2.isPrimary) {
        return notifyWarn("Please select a primary guardian");
    }
  }

  // Prepare payload - combine country code + number into contactNumber
  const payload = {
    guardians: showSecondary.value 
      ? [ { name: g1.name, contactNumber: buildContactNumber(g1), email: g1.email, isPrimary: g1.isPrimary },
          { name: g2.name, contactNumber: buildContactNumber(g2), email: g2.email, isPrimary: g2.isPrimary } ]
      : [ { name: g1.name, contactNumber: buildContactNumber(g1), email: g1.email, isPrimary: g1.isPrimary } ]
  };

  emit("parents-details-selected", payload);
};
</script>

<style scoped>
.required-astrterick {
  color: #EF4444;
  margin-left: 2px;
}

.orange-button-text {
  color: #ec0e37;
  font-size: 12px;
  margin-bottom: 8px;
}

.phone-country-select {
  min-width: 140px;
  max-width: 180px;
}
</style>